import React, { useEffect, useState } from "react";
import { EmailTemplate } from "../types";

// Import ReactQuill
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css"; // Import Quill styles

// Add custom styles for the editor
import "./EmailTemplateEditor.css";

interface EmailTemplateEditorProps {
  content: string; // This will now be the HTML content string
  onChange?: (htmlContent: string) => void; // onChange will only return HTML content
  variables?: string[];
  template?: EmailTemplate | null; // Keep template for potential future use (e.g., variable insertion)
}

export function EmailTemplateEditor({
  content,
  onChange,
  variables = [],
  template,
}: EmailTemplateEditorProps) {
  const [editorContent, setEditorContent] = useState<string>(content);

  // Update internal state when the content prop changes (e.g., loading a new template)
  useEffect(() => {
    setEditorContent(content);
  }, [content]);

  // Handle changes from ReactQuill
  const handleQuillChange = (html: string) => {
    setEditorContent(html);
    if (onChange) {
      onChange(html);
    }
  };

  // Configure Quill modules (toolbar options)
  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      ["link", "image"],
      ["clean"],
    ],
  };

  // Configure Quill formats (allowed tags)
  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
  ];

  return (
    // Add a class to the container div for styling
    <div className="email-editor-container h-full flex flex-col">
      {/* Variable insertion (optional - can be implemented as a custom toolbar button or a separate list) */}
      {variables.length > 0 && (
        <div className="mb-2">
          <strong>Available Variables:</strong>
          <span className="ml-2 text-sm text-muted-foreground">
            {variables.map((v) => `[${v}]`).join(", ")}
          </span>
        </div>
      )}
      <ReactQuill
        theme="snow" // Use the 'snow' theme for a standard toolbar
        value={editorContent}
        onChange={handleQuillChange}
        modules={modules}
        formats={formats}
        className="flex-grow" // Allow the editor to grow and fill the space
        // The min-height is now set in the CSS file
      />
      {/* You might want a separate preview area here if needed */}
    </div>
  );
}
